/* eslint-disable @typescript-eslint/no-unused-vars */
import { useEffect, useState } from 'react';
import {
  Alert, Autocomplete, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Grid, IconButton, LinearProgress, List, ListItem, Paper, Snackbar, SnackbarCloseReason,
  Table, TableBody, TableCell, TableContainer, TableFooter, TableHead, TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { v4 as uuidv4 } from 'uuid';
import * as yup from 'yup';
import { VTextField, VForm, useVForm, IVFormErrors, AutoCompleteParceiro, VTextFieldData, VFieldMoney, AutoCompletePlanoDeContas, } from '../../../shared/forms';
import { FerramentasDeDetalhe, TelaBloqueioSalvando } from '../../../shared/components';
import { useBloqueioSalvamento } from '../../../shared/hooks';
import { LayoutBaseDePagina } from '../../../shared/layouts';
import { ContasReceberService, IContaConsolidadaFilho, IRecebidoHistorico, IParcelaDoLote } from '../../../shared/services/api/Financeiro/ContasReceber';
import { EmpresaService } from '../../../shared/services/api/cadastros/Empresa';
import { IComprovanteBaixaDados } from './ComprovanteBaixa';
import { DialogVisualizarComprovante } from './DialogVisualizarComprovante';
import { formatarMoeda } from '../../../shared/utils/formatarMoeda';

// Sugestões do campo único de "Parcelas" (1x a 36x) — o mesmo campo também
// aceita digitar qualquer quantidade (até MAX_PARCELAS_MANUAL): conforme o
// usuário digita, a lista de parcelas é gerada ao vivo (ver
// handleParcelasTexto). Editando uma parcela avulsa antiga (sem idlote), o
// campo também aceita o rótulo de texto livre de sempre (ex.: "1/3").
const OPCOES_QTD_PARCELAS = Array.from({ length: 36 }, (_, i) => String(i + 1));
// Teto de segurança pra digitação manual — sem isso, um número digitado por
// engano (ex.: alguns zeros a mais) travaria a tela gerando milhares de
// parcelas. 120 já cobre um parcelamento mensal de 10 anos, bem acima de
// qualquer caso real de Contas a Receber.
const MAX_PARCELAS_MANUAL = 120;

const arredondar2 = (valor: number): number => Math.round(valor * 100) / 100;

// Campo de dinheiro totalmente controlado pelo `valor` (prop), diferente do
// VFieldMoney2Faturamento (Estoque/venda) — aquele só lê o valor inicial na
// primeira montagem (defaultValue) e ignora atualizações depois, então
// quando o "Valor Total"/quantidade de parcelas mudava e a lista era
// recalculada por baixo dos panos, o campo continuava mostrando o número
// antigo (bug reportado: "delay" entre o que tem no Valor Total e o que
// aparece em cada parcela). Aqui o texto exibido vem sempre de `valor`, sem
// estado interno — qualquer recálculo aparece na hora, com o número exato.
interface ICampoValorParcelaProps {
  valor: number;
  disabled?: boolean;
  aoAlterar: (novoValor: number) => void;
}
const CampoValorParcela: React.FC<ICampoValorParcelaProps> = ({ valor, disabled, aoAlterar }) => (
  <TextField
    size='small'
    fullWidth
    label='Valor da parcela'
    disabled={disabled}
    value={valor.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
    onChange={(e) => {
      const digitos = e.target.value.replace(/\D/g, '');
      aoAlterar(digitos ? Number(digitos) / 100 : 0);
    }}
  />
);

// Uma linha da lista de parcelas geradas (só existe quando qtd. de parcelas >
// 1 num lançamento NOVO — ver listaParcelas). "key" é só identificador local
// pra edição/renderização, não é enviado ao backend.
interface IParcelaGerada {
  key: string;
  parcela: string;
  // Número da nota dessa parcela — vazio usa o "Numero Nota" do cabeçalho
  // (ou, sem ele, o número gerado automaticamente, ver ContasReceberService.create).
  numero: string;
  vencimento: string;
  vparcela: number;
  // Desconto/acréscimo total do lançamento (campos do cabeçalho) dividido
  // igualmente entre as parcelas — ver useEffect de listaParcelas.
  desconto: number;
  acrescimo: number;
}

interface IFormData {
  idparceiro: string;
  idplanoconta: string;
  vencimento: Date;
  emissao: Date;
  parcela: string;
  numero: string;
  venda: number;
  vparcela: number;
  vrecebido: number;
  acrescimo: number;
  juros: number;
  desconto: number;
  vreceber: number;
  historico: string;
}
const formValidationSchema: yup.ObjectSchema<IFormData> = yup.object().shape({
  idparceiro: yup.string().required(),
  idplanoconta: yup.string().required(),
  emissao: yup.date().required(),
  vencimento: yup.date().required('O campo é obrigatório')
    .when('$isEditMode', {
      is: false,
      then: () => yup.date().test('valid-date', 'Não se Lança conta já Vencida', function (value) {
        if (!value) {
          return this.createError({ message: 'Data inválida' });
        }
        const currentDate = new Date();
        currentDate.setDate(currentDate.getDate() - 5);
        return value >= currentDate;
      }),
    }),
  numero: yup.string().default(''),
  parcela: yup.string().required(),
  venda: yup.number().required(),
  vparcela: yup.number().required().test('valorparcela', 'o valor deve ser menor ou igual ao valor total e diferente de zero', function (value, context) {
    const valorcompra = context.parent.venda || 0;
    return value <= valorcompra && value !== 0;
  }),
  vrecebido: yup.number().required().default(0),
  acrescimo: yup.number().required(),
  juros: yup.number().required().default(0),
  desconto: yup.number().required(),
  vreceber: yup.number().required(),
  historico: yup.string().optional().default(''),
});

export const DetalheDeReceber: React.FC = () => {
  const { id = 'novo' } = useParams<'id'>();
  const navigate = useNavigate();
  const location = useLocation();
  const { formRef, save, saveAndClose} = useVForm();
  const [isLoading, setIsLoading] = useState(false);
  const { isSaving, progress: saveProgress, iniciar: iniciarSalvamento, concluir: concluirSalvamento, cancelar: cancelarSalvamento } = useBloqueioSalvamento();
  const [descricao, setDescricao] = useState('');
  const [statusVenda, setStatusVenda] = useState(false);
  const [statusRecebidas, setStatusRecebidas] = useState(false);
  const [idparceiro, setIdParceiro] = useState('');
  // Nome do parceiro selecionado — só pra levar de volta pra listagem (ver
  // navegarParaListagem) e ela conseguir mostrar o parceiro certo no campo de
  // busca, em vez de voltar em branco (ver AutoCompleteParceiroListagem.tsx).
  const [nomeParceiro, setNomeParceiro] = useState('');
  // Texto do campo único de "Parcelas" (escolhido na lista ou digitado) —
  // controlado à parte porque não é necessariamente o valor final salvo
  // (isso é qtdParcelas/o campo 'parcela'), ver handleParcelasTexto.
  const [parcelasTexto, setParcelasTexto] = useState('');
  // Quantidade escolhida no campo de Parcelas — só dispara a geração
  // automática de várias parcelas (listaParcelas) num lançamento NOVO com
  // valor > 1 (ver useEffect logo abaixo de handleParcelasTexto).
  const [qtdParcelas, setQtdParcelas] = useState(1);
  // "Valor Total" do lançamento (campo 'venda') espelhado aqui — só existe
  // pra recalcular listaParcelas quando o usuário digita (ver handleValorTotal).
  const [valorTotal, setValorTotal] = useState(0);
  // "Acréscimo / Juros" e "Desconto" do cabeçalho espelhados aqui — com mais
  // de 1 parcela, cada um é dividido igualmente entre as parcelas geradas
  // (ver useEffect de listaParcelas e handlecalcularAcescimo/handlecalcularDesconto).
  const [acrescimoTotal, setAcrescimoTotal] = useState(0);
  const [descontoTotal, setDescontoTotal] = useState(0);
  // Lista de parcelas geradas quando qtdParcelas > 1 — uma conta a receber é
  // criada (lançamento novo) ou o grupo inteiro é apagado e recriado
  // (lançamento existente, ver alterandoQtdParcelas) por item dessa lista
  // (ver handleSave).
  const [listaParcelas, setListaParcelas] = useState<IParcelaGerada[]>([]);
  // Vincula esse registro às parcelas irmãs geradas juntas (mesmo
  // lançamento parcelado, ver migrarTenant.ts, backend) — vazio quando é uma
  // parcela única. `outrasParcelas` alimenta a listagem "Outras parcelas
  // desse lançamento" (com valor e situação de cada uma).
  const [idlote, setIdlote] = useState('');
  const [outrasParcelas, setOutrasParcelas] = useState<IParcelaDoLote[]>([]);
  // true só depois que o usuário mexe de propósito no seletor de Parcelas
  // editando um lançamento já existente — é isso que distingue "abrir uma
  // parcela pra editar campos normais" de "trocar a quantidade de parcelas"
  // (que dispara apagar+recriar o grupo inteiro, ver handleSave e a regra
  // combinada com o usuário: só a TROCA DE QUANTIDADE cascateia pras
  // demais parcelas, um campo comum editado sozinho não mexe nas outras).
  const [alterandoQtdParcelas, setAlterandoQtdParcelas] = useState(false);
  const [recebidas, setRecebidas] = useState(0);
  const [filhos, setFilhos] = useState<IContaConsolidadaFilho[]>([]);
  const [recebidosHistorico, setRecebidosHistorico] = useState<IRecebidoHistorico[]>([]);
  const [comprovanteDados, setComprovanteDados] = useState<IComprovanteBaixaDados | null>(null);
  const [openComprovante, setOpenComprovante] = useState(false);
  const [openmsg, setOpenmsg] = useState(false);
  const [msg, setMsg] = useState('');
  const [tms, setTms] = useState<'warning' | 'error' | 'info' | 'success'>('warning');

  const [openConfirm, setOpenConfirm] = useState(false);
  const [apagando, setApagando] = useState(false);
  // Dados desta própria conta pra lista da confirmação de exclusão (as
  // demais parcelas do lançamento vêm em `outrasParcelas`).
  const [parcelaAtual, setParcelaAtual] = useState<{ parcela: string | null; numero: string; vencimento: Date; vparcela: number } | null>(null);

  // Regras de edição de uma parcela que faz parte de um lançamento parcelado
  // (mesmo idlote) — combinadas com o usuário:
  // - Se ESSA parcela já teve recebimento (total ou parcial, statusRecebidas),
  //   só o campo Desconto pode mudar (ver disabled dos demais campos, JSX).
  // - Senão, se ALGUMA OUTRA parcela do grupo já teve recebimento, só
  //   Desconto/Acréscimo/Histórico podem mudar — vencimento, valor, parceiro,
  //   plano de contas e a própria quantidade de parcelas ficam travados.
  // - Só quando NADA no grupo foi recebido ainda é que a quantidade de
  //   parcelas pode ser alterada (ver alterandoQtdParcelas) — trocar a
  //   quantidade apaga o grupo inteiro e gera parcelas novas (handleSave).
  const outroTemPagamentoNoGrupo = outrasParcelas.some((p) => Number(p.vrecebido) > 0 || p.status === true);
  const bloqueioParcelamento = statusVenda || statusRecebidas || outroTemPagamentoNoGrupo;
  // Mesma regra do backend (contasreceber/DeleteById.ts): conta de venda não
  // se apaga por aqui; parcelado só sai inteiro, e nem isso se qualquer
  // parcela já teve recebimento; conta consolidada também não.
  const bloqueioApagar = statusVenda || statusRecebidas || outroTemPagamentoNoGrupo || filhos.length > 0;
  // Todas as parcelas que vão junto na exclusão (esta + as do mesmo
  // lançamento), em ordem de vencimento.
  const parcelasParaApagar = [
    ...(parcelaAtual ? [{ idrec: id, ...parcelaAtual }] : []),
    ...outrasParcelas.map((p) => ({ idrec: p.idrec, parcela: p.parcela, numero: p.numero || '', vencimento: p.vencimento, vparcela: Number(p.vparcela) })),
  ].sort((a, b) => dayjs(a.vencimento).valueOf() - dayjs(b.vencimento).valueOf());

  // Reconstrói os dados do comprovante a partir de uma linha do histórico
  // de recebimentos — sem depender de nenhum estado guardado no momento da
  // baixa original, já que estamos reabrindo o título bem depois.
  const handleReimprimir = async (item: IRecebidoHistorico) => {
    const empresaResult = await EmpresaService.getById();
    const empresa = !(empresaResult instanceof Error) ? empresaResult : undefined;
    // O histórico do lançamento (o que é essa conta, ex.: "Mensalidade do
    // sistema referente a setembro/2026") já está carregado no form desta
    // própria tela (ver formValidationSchema/`historico`) — não precisa de
    // outra chamada, é o mesmo lançamento sendo visualizado.
    const historico = formRef.current?.getData()?.historico as string | undefined;
    setComprovanteDados({
      parceiro: item.nomeParceiro || '-',
      contaNome: item.nomeConta || '-',
      datalan: item.dataoperacao,
      contas: [{ numero: item.numero || '', valor: Number(item.valor), historico }],
      // Nem toda baixa antiga guarda o detalhamento por forma de pagamento
      // (só quando foi parcial, ou total com "Juntar Lançamentos" marcado —
      // ver GetRecebidos.ts no backend); sem isso, cai num resumo genérico
      // com só o valor total recebido.
      formasPagamento: item.formasPagamento.length > 0
        ? item.formasPagamento.map((f) => ({ nome: f.historico, valor: Number(f.valor) }))
        : [{ nome: 'Recebimento', valor: Number(item.valor) }],
      totalBaixado: Number(item.valor),
      desconto: 0,
      acrescimo: 0,
      empresaFantasia: empresa?.fantasia,
      empresaRazaoSocial: empresa?.nome,
    });
    setOpenComprovante(true);
  };

  // Volta pra listagem já filtrada pelo parceiro atual (o que veio
  // pré-selecionado, o que o usuário escolheu/alterou, ou o de um registro já
  // existente) — tanto depois de salvar quanto no botão "Voltar", ela já
  // reaparece com o mesmo parceiro pesquisado, sem "zerar" o filtro.
  const navegarParaListagem = () => {
    if (idparceiro) {
      const parametros = new URLSearchParams({
        basicselect: 'parceiro',
        parceiroselect: idparceiro,
        parceiro: nomeParceiro || '',
        pagina: '1',
      });
      navigate(`/contasreceber?${parametros.toString()}`);
    } else {
      navigate('/contasreceber');
    }
  };

  const handleCloseMsg = (
    event?: React.SyntheticEvent | Event,
    reason?: SnackbarCloseReason,
  ) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpenmsg(false);
    if (tms === 'success') {
      navegarParaListagem();
    }
  };

  const handleDelete = () => {
    setOpenConfirm(true);
  };

  // Sempre apaga o lançamento inteiro (todas as parcelas vinculadas).
  const handleConfirmDelete = () => {
    if (apagando) return;
    setApagando(true);
    ContasReceberService.deleteById(id)
      .then(result => {
        setApagando(false);
        if (result instanceof Error) {
          setOpenConfirm(false);
          setTms('error');
          setMsg(result.message);
          setOpenmsg(true);
        } else {
          setOpenConfirm(false);
          setTms('success');
          setMsg(parcelasParaApagar.length > 1 ? 'Todas as parcelas foram apagadas com sucesso!' : 'Registro apagado com sucesso!');
          setOpenmsg(true);
        }
      });
  };
  const handleCancelDelete = () => {
    setOpenConfirm(false);
  };
  useEffect(() => {
    if (id !== 'novo') {
      setIsLoading(true);
      ContasReceberService.getById(id)
        .then((result) => {
          setIsLoading(false);
          if (result instanceof Error) {
            setTms('error');
            setMsg(result.message);
            setOpenmsg(true);
          } else {
            setDescricao('Lançamento Contas Receber');
            const dataToForm = {
              ...result,
              numero: result.numero === null ? '' : result.numero
            };
            formRef.current?.setData(dataToForm);
            setAlterandoQtdParcelas(false);
            setListaParcelas([]);
            setIdlote(result.idlote || '');
            const outras = result.outrasParcelas || [];
            setOutrasParcelas(outras);
            // "Valor Total"/desconto/acréscimo do GRUPO inteiro — necessário
            // já carregado pra quando o usuário trocar a quantidade de
            // parcelas (ver alterandoQtdParcelas): `venda` já é o total
            // (repetido em cada parcela do grupo), mas desconto/acréscimo são
            // a fatia de CADA parcela, então soma com as outras pra
            // reconstruir o total do lançamento.
            setValorTotal(Number(result.venda) || 0);
            setDescontoTotal(Number(result.desconto || 0) + outras.reduce((acc, p) => acc + Number(p.desconto || 0), 0));
            setAcrescimoTotal(Number(result.acrescimo || 0) + outras.reduce((acc, p) => acc + Number(p.acrescimo || 0), 0));
            if (result.idlote) {
              // Parcela de um lançamento parcelado: o seletor de "Parcelas"
              // passa a representar o TAMANHO DO GRUPO (pra permitir trocar a
              // quantidade, ver handleParcelasTexto),
              // não o rótulo "i/N" dessa parcela específica.
              const tamanhoGrupo = 1 + outras.length;
              setParcelasTexto(String(tamanhoGrupo));
              setQtdParcelas(tamanhoGrupo);
            } else {
              // Parcela avulsa (sem grupo): parcela salva como número puro de
              // 1 a 36 => BasicSelect; qualquer outro formato (ex.: "1/3",
              // legado) => modo manual, com o valor já salvo, texto livre.
              setParcelasTexto(String(result.parcela ?? '').trim());
              setQtdParcelas(1);
            }
            setIdParceiro(result.idparceiro);
            setRecebidas(result.recebido);
            setFilhos(result.filhos || []);
            setParcelaAtual({
              parcela: result.parcela ?? null,
              numero: result.numero ?? '',
              vencimento: result.vencimento,
              vparcela: Number(result.vparcela) || 0,
            });
            if (result.tipo == 1) {
              setStatusVenda(true);
            } else {
              setStatusVenda(false);
            }
            if (result.vrecebido > 0) {
              setStatusRecebidas(true);
              ContasReceberService.getRecebidos(id).then((historico) => {
                if (!(historico instanceof Error)) setRecebidosHistorico(historico);
              });
            } else {
              setStatusRecebidas(false);
            }
          }
        });
    } else {
      setFilhos([]);
      setRecebidosHistorico([]);
      setParcelasTexto('');
      setQtdParcelas(1);
      setValorTotal(0);
      setAcrescimoTotal(0);
      setDescontoTotal(0);
      setListaParcelas([]);
      setIdlote('');
      setOutrasParcelas([]);
      setAlterandoQtdParcelas(false);
      // Parceiro filtrado na listagem (ver aoClicarEmNovo em ListagemContasReceber.tsx)
      // já vem pré-selecionado pra um novo lançamento.
      const idParceiroPreSelecionado = (location.state as { idparceiro?: string } | null)?.idparceiro;
      setIdParceiro(idParceiroPreSelecionado || '');
      formRef.current?.setData({
        idparceiro: idParceiroPreSelecionado,
        emissao: new Date(),
        vencimento: new Date(),
        historico: '',
        numero: '',
        venda: 0,
        valorparcela: 0,
        juros: 0,
        acrescimo: 0,
        desconto: 0,
        vrecebido: 0,
        vreceber: 0,
        vparcela: 0,

      });
    }
  }, [id]);

  const handleSave = (dados: IFormData) => {
    if (!iniciarSalvamento()) return;

    const dadosSemAuxiliar = dados;

    // Sem "Número Nota" preenchido, cada lançamento (cada parcela, no modo de
    // múltiplas parcelas) recebe seu próprio número gerado a partir do uuid
    // (idrec) daquele registro específico — ver ContasReceberService.create.

    const validationContext = { isEditMode: id === 'novo' ? false : true };
    formValidationSchema.
      validate(dadosSemAuxiliar, { context: validationContext, abortEarly: false })
      .then((dadosValidados) => {
        setIsLoading(true);
        // Sincroniza com o parceiro de fato salvo (pode ter sido trocado pelo
        // usuário) — é ele que decide o filtro da listagem no handleCloseMsg.
        setIdParceiro(dadosValidados.idparceiro);
        if (id === 'novo' && listaParcelas.length > 1) {
          // Uma conta a receber por parcela da lista — mesmo parceiro/plano de
          // contas/emissão/histórico/valor total; vencimento, valor e o rótulo
          // da parcela mudam por item, e o Acréscimo/Desconto do cabeçalho vai
          // dividido igualmente entre elas (ver useEffect de listaParcelas).
          // Mesmo idlote em todas as parcelas dessa leva — é o vínculo que
          // permite achar as irmãs (apagar todas juntas, travar edição quando
          // uma já foi recebida — ver DeleteById.ts/UpdateById.ts, backend).
          const idloteNovo = uuidv4();
          (async () => {
            try {
              for (const item of listaParcelas) {
                const resultado = await ContasReceberService.create({
                  ...dadosValidados,
                  idlote: idloteNovo,
                  numero: item.numero.trim() || dadosValidados.numero,
                  vencimento: new Date(`${item.vencimento}T00:00:00`),
                  parcela: item.parcela,
                  vparcela: item.vparcela,
                  acrescimo: item.acrescimo,
                  desconto: item.desconto,
                  vreceber: arredondar2(item.vparcela - item.desconto + item.acrescimo),
                  vrecebido: 0,
                });
                if (resultado instanceof Error) throw resultado;
              }
              setIsLoading(false);
              setTms('success');
              setMsg(`${listaParcelas.length} parcelas lançadas com sucesso!`);
              setOpenmsg(true);
              concluirSalvamento();
            } catch (erro) {
              setIsLoading(false);
              cancelarSalvamento();
              setTms('error');
              setMsg(erro instanceof Error ? erro.message : 'Erro ao gerar as parcelas.');
              setOpenmsg(true);
            }
          })();
        } else if (id === 'novo') {
          ContasReceberService
            .create(dadosValidados)
            .then((result) => {
              setIsLoading(false);
              if (result instanceof Error) {
                cancelarSalvamento();
                setTms('error');
                setMsg(result.message);
                setOpenmsg(true);
              } else {
                setTms('success');
                setMsg('Lançamento Realizado com sucesso!');
                setOpenmsg(true);
                concluirSalvamento();
              }
            });
        } else if (alterandoQtdParcelas && listaParcelas.length > 1) {
          // Usuário trocou a quantidade de parcelas de um lançamento já
          // existente (ver handleParcelasTexto) — o
          // backend apaga o grupo inteiro (esse registro e as parcelas
          // irmãs, mesmo idlote) e cria essas no lugar; só é aceito se
          // nenhuma parcela do grupo já tiver recebimento (ver
          // RegenerarParcelas.ts, backend).
          const parcelas = listaParcelas.map((item) => {
            const guuid = uuidv4();
            const numero = item.numero.trim()
              || dadosValidados.numero?.trim()
              || String(parseInt(guuid.replace(/-/g, '').slice(0, 8), 16));
            return {
              ...dadosValidados,
              idrec: guuid,
              tipo: 2,
              numero,
              vencimento: new Date(`${item.vencimento}T00:00:00`),
              parcela: item.parcela,
              vparcela: item.vparcela,
              acrescimo: item.acrescimo,
              desconto: item.desconto,
              vreceber: arredondar2(item.vparcela - item.desconto + item.acrescimo),
              vrecebido: 0,
            };
          });
          ContasReceberService
            .regenerarParcelas(id, parcelas)
            .then((result) => {
              setIsLoading(false);
              if (result instanceof Error) {
                cancelarSalvamento();
                setTms('error');
                setMsg(result.message);
                setOpenmsg(true);
              } else {
                setTms('success');
                setMsg(`${listaParcelas.length} parcelas geradas novamente com sucesso!`);
                setOpenmsg(true);
                concluirSalvamento();
              }
            });
        } else {
          ContasReceberService
            .updateById(id, { recebido: recebidas, ...dadosValidados })
            .then((result) => {
              setIsLoading(false);
              if (result instanceof Error) {
                cancelarSalvamento();
                setTms('error');
                setMsg(result.message);
                setOpenmsg(true);
              } else {
                setTms('success');
                setMsg('Alteração concluída com sucesso!');
                setOpenmsg(true);
                concluirSalvamento();
              }
            });
        }
      })
      .catch((errors: yup.ValidationError) => {
        cancelarSalvamento();
        const validationErrors: IVFormErrors = {};
        errors.inner.forEach(error => {
          if (!error.path) return;
          validationErrors[error.path] = error.message;
        });
        formRef.current?.setErrors(validationErrors);
      });
  };

  // Campo único de "Parcelas" — tanto escolher uma sugestão (1x a 36x)
  // quanto digitar caem aqui. Número vira a quantidade de parcelas, ao vivo
  // (qtdParcelas dispara o useEffect que gera/regera listaParcelas), limitado
  // a MAX_PARCELAS_MANUAL por segurança; com 0 ou 1, é parcela única (rótulo
  // salvo "1"). Editando uma parcela avulsa antiga (sem idlote), texto que
  // não é número (ex.: "1/3") é o rótulo livre de sempre, salvo direto no
  // campo 'parcela'. Num lançamento novo ou com idlote só aceita dígitos.
  const handleParcelasTexto = (texto: string) => {
    if (id !== 'novo') {
      if (bloqueioParcelamento) return;
      setAlterandoQtdParcelas(true);
    }
    const aceitaRotuloLivre = id !== 'novo' && !idlote;
    const limpo = texto.trim().replace(/x$/i, '');
    if (aceitaRotuloLivre && !/^\d*$/.test(limpo)) {
      setParcelasTexto(texto.slice(0, 20));
      setQtdParcelas(1);
      formRef.current?.setFieldValue('parcela', texto.trim().slice(0, 20));
      return;
    }
    const somenteDigitos = limpo.replace(/\D/g, '').slice(0, 3);
    setParcelasTexto(somenteDigitos);
    const quantidade = somenteDigitos ? Math.min(MAX_PARCELAS_MANUAL, parseInt(somenteDigitos, 10)) : 0;
    setQtdParcelas(quantidade > 0 ? quantidade : 1);
    if (quantidade <= 1 && (id === 'novo' || !idlote)) {
      formRef.current?.setFieldValue('parcela', quantidade === 1 ? '1' : '');
    }
    // quantidade >= 2: o useEffect de listaParcelas já sincroniza o campo
    // 'parcela' com o rótulo da 1ª parcela gerada — não precisa fazer aqui.
  };
  // Espelha o campo "Valor Total" (venda) num state — só pra recalcular
  // listaParcelas quando o usuário digita (ver useEffect abaixo).
  const handleValorTotal = (valor: string | undefined) => {
    if (valor != null) {
      valor = valor.replace(/\D/g, '');
      valor = valor.replace(/(\d)(\d{2})$/, '$1.$2');
      valor = valor.replace(/(?=(\d{3})+(\D))\B/g, '');
      setValorTotal(Number(valor));
    }
  };
  // Gera (ou refaz do zero) a lista de parcelas quando o valor total ou a
  // quantidade de parcelas mudam — num lançamento NOVO, sempre que tem mais
  // de 1 parcela; num lançamento já existente, só depois que o usuário mexeu
  // de propósito no seletor de Parcelas (alterandoQtdParcelas, ver
  // handleParcelasTexto) — nesse caso o grupo inteiro
  // é apagado e recriado com essas parcelas (ver handleSave).
  // Vencimentos default: 30, 60, 90... dias a partir de hoje (mesma regra do
  // Faturamento de Venda — ver DialogFaturamento.tsx); valor de cada parcela é
  // o total dividido igualmente, com a última ajustada pra fechar a soma exata.
  useEffect(() => {
    const podeGerar = id === 'novo' || alterandoQtdParcelas;
    if (!podeGerar || qtdParcelas <= 1) {
      setListaParcelas([]);
      return;
    }
    const valorBase = arredondar2(valorTotal / qtdParcelas);
    const descontoBase = arredondar2(descontoTotal / qtdParcelas);
    const acrescimoBase = arredondar2(acrescimoTotal / qtdParcelas);
    let somaParcial = 0;
    let somaDesconto = 0;
    let somaAcrescimo = 0;
    const novaLista: IParcelaGerada[] = [];
    for (let i = 0; i < qtdParcelas; i++) {
      const ehUltima = i === qtdParcelas - 1;
      const valor = ehUltima ? arredondar2(valorTotal - somaParcial) : valorBase;
      const desconto = ehUltima ? arredondar2(descontoTotal - somaDesconto) : descontoBase;
      const acrescimo = ehUltima ? arredondar2(acrescimoTotal - somaAcrescimo) : acrescimoBase;
      somaParcial += valor;
      somaDesconto += desconto;
      somaAcrescimo += acrescimo;
      const dataVencimento = dayjs().add(30 + i * 30, 'day').format('YYYY-MM-DD');
      novaLista.push({
        key: String(i),
        parcela: `${i + 1}/${qtdParcelas}`,
        numero: '',
        vencimento: dataVencimento,
        vparcela: valor,
        desconto,
        acrescimo,
      });
    }
    // Recalcular valores (trocar total/quantidade) não apaga os números de
    // nota já digitados — mantém o de cada posição que continuar existindo.
    setListaParcelas((anterior) => novaLista.map((item, i) => ({ ...item, numero: anterior[i]?.numero ?? '' })));
    // Campos "reais" (vencimento/vparcela/parcela) ficam desabilitados nesse
    // modo (ver JSX), mas continuam obrigatórios pro yup — espelham a 1ª
    // parcela só pra passar na validação; o valor de fato usado por parcela
    // vem de listaParcelas (ver handleSave).
    formRef.current?.setFieldValue('vencimento', novaLista[0]?.vencimento);
    formRef.current?.setFieldValue('vparcela', novaLista[0]?.vparcela);
    formRef.current?.setFieldValue('parcela', novaLista[0]?.parcela);
  }, [id, alterandoQtdParcelas, qtdParcelas, valorTotal, descontoTotal, acrescimoTotal]);
  const handleParcelaGeradaVencimento = (key: string, novoValor: string) => {
    setListaParcelas((atual) => atual.map((item) => item.key === key ? { ...item, vencimento: novoValor } : item));
  };
  const handleParcelaGeradaNumero = (key: string, novoValor: string) => {
    setListaParcelas((atual) => atual.map((item) => item.key === key ? { ...item, numero: novoValor.slice(0, 30) } : item));
  };
  const handleParcelaGeradaValor = (key: string, novoValor: string) => {
    setListaParcelas((atual) => atual.map((item) => item.key === key ? { ...item, vparcela: arredondar2(Number(novoValor) || 0) } : item));
  };
  const handlecalcularParcela = (valor: string | undefined) => {
    if (valor != null) {
      valor = valor.replace(/\D/g, '');
      valor = valor.replace(/(\d)(\d{2})$/, '$1.$2');
      valor = valor.replace(/(?=(\d{3})+(\D))\B/g, '');
      const data = formRef.current?.getData();
      const acrescimo = data?.acrescimo;
      const desconto = data?.desconto;
      const recebido = data?.vrecebido;
      const tparcela = Number(valor);
      const vreceber = tparcela + Number(acrescimo) - Number(desconto) - Number(recebido);
      formRef.current?.setFieldValue('vreceber', vreceber);
    }
  };
  const handlecalcularAcescimo = (valor: string | undefined) => {
    if (valor != null) {
      valor = valor.replace(/\D/g, '');
      valor = valor.replace(/(\d)(\d{2})$/, '$1.$2');
      valor = valor.replace(/(?=(\d{3})+(\D))\B/g, '');
      const acrescimo = Number(valor);
      // Com mais de 1 parcela, esse total é dividido entre elas (ver useEffect
      // de listaParcelas) — aqui só espelha pra disparar o recálculo.
      setAcrescimoTotal(acrescimo);
      const data = formRef.current?.getData();
      const vparcela = data?.vparcela;
      const desconto = data?.desconto;
      const recebido = data?.vrecebido;
      const vreceber = acrescimo + Number(vparcela) - Number(desconto) - Number(recebido);
      formRef.current?.setFieldValue('vreceber', vreceber);
    }
  };
  const handlecalcularDesconto = (valor: string | undefined) => {
    if (valor != null) {
      valor = valor.replace(/\D/g, '');
      valor = valor.replace(/(\d)(\d{2})$/, '$1.$2');
      valor = valor.replace(/(?=(\d{3})+(\D))\B/g, '');
      const desconto = Number(valor);
      // Com mais de 1 parcela, esse total é dividido entre elas (ver useEffect
      // de listaParcelas) — aqui só espelha pra disparar o recálculo.
      setDescontoTotal(desconto);
      const data = formRef.current?.getData();
      const parcela = data?.vparcela;
      const acrescimo = data?.acrescimo;
      const recebido = data?.vrecebido;
      const vreceber = Number(parcela) + Number(acrescimo) - desconto - Number(recebido);
      formRef.current?.setFieldValue('vreceber', vreceber);
    }
  };
  const modoMultiplasParcelas = qtdParcelas > 1 && (id === 'novo' || alterandoQtdParcelas);
  // Soma do valor líquido (principal - desconto + acréscimo) de cada parcela
  // gerada, comparado com o valor líquido esperado do lançamento inteiro.
  const somaListaParcelas = listaParcelas.reduce((acc, item) => acc + (item.vparcela - item.desconto + item.acrescimo), 0);
  const valorLiquidoEsperado = arredondar2(valorTotal - descontoTotal + acrescimoTotal);
  const diferencaListaParcelas = arredondar2(somaListaParcelas - valorLiquidoEsperado);
  return (
    <>
      <TelaBloqueioSalvando
        open={isSaving}
        message={id === 'novo' ? 'Salvando lançamento...' : 'Salvando alterações...'}
        progress={saveProgress}
      />
      <LayoutBaseDePagina
      titulo={id === 'novo' ? 'Novo Lançamento Contas Receber' : descricao}
      barraDeFerramentas={
        <FerramentasDeDetalhe
          textoBotaoNovo='Novo'
          mostrarBotaoSalvarEFechar={false}
          mostrarBotaoSalvarCarregando={isLoading}
          mostrarBotaoSalvarEFecharCarregando={isLoading}
          mostrarBotaoNovo={id !== 'novo'}
          mostrarBotaoApagar={id !== 'novo'}
          mostrarBotaoApagarCarregando={isLoading}
          mostrarBotaoImpressao={false}
          aoClicarEmSalvar={save}
          aoClicarEmSalvarEFechar={saveAndClose}
          aoClicarEmVoltar={navegarParaListagem}
          mostrarBotaoVoltarCarregando={isLoading}
          aoClicarEmApagar={() => handleDelete()}
          aoClicarEmNovo={() => navigate('/contasreceber/novo')}
          mostrarBotaoNovoCarregando={isLoading}
          disableBotaoApagar={bloqueioApagar}
          disableBotaoSalvar={isLoading || isSaving || (modoMultiplasParcelas && Math.abs(diferencaListaParcelas) > 0.001)}
        />
      }
    >
      <VForm ref={formRef} onSubmit={handleSave} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
        <Snackbar anchorOrigin={{ vertical: 'top', horizontal: 'center' }} open={openmsg} autoHideDuration={tms === 'error' ? null : 900} onClose={handleCloseMsg}>
          <Alert
            onClose={handleCloseMsg}
            severity={tms}
            variant="filled"
            sx={{ width: '100%' }}
          >
            {msg}
          </Alert>
        </Snackbar>
        {/* Confirmação de exclusão: antes de perguntar, mostra TODAS as
            parcelas do lançamento que vão junto com esta — parcelas não
            podem ser apagadas separadamente (ver DeleteById.ts, backend). */}
        <Dialog open={openConfirm} onClose={() => { if (!apagando) handleCancelDelete(); }} fullWidth maxWidth="sm" PaperProps={{ sx: { borderRadius: 3 } }}>
          <DialogTitle sx={{ fontWeight: 700 }}>
            {parcelasParaApagar.length > 1 ? 'Apagar lançamento parcelado' : 'Apagar conta a receber'}
          </DialogTitle>
          <DialogContent dividers>
            {parcelasParaApagar.length > 1 ? (
              <>
                <Alert severity="warning" variant="outlined" sx={{ mb: 2 }}>
                  Esta conta faz parte de um lançamento com <b>{parcelasParaApagar.length} parcelas</b>.
                  Parcelas não podem ser apagadas separadamente — todas abaixo serão apagadas juntas.
                </Alert>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Parcela</TableCell>
                      <TableCell>Nº Documento</TableCell>
                      <TableCell>Vencimento</TableCell>
                      <TableCell align="right">Valor</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {parcelasParaApagar.map((p) => {
                      const atual = p.idrec === id;
                      return (
                        <TableRow key={p.idrec} selected={atual}>
                          <TableCell>
                            {p.parcela || '-'}
                            {atual && <Chip label="Esta" size="small" color="primary" sx={{ ml: 1, height: 18, fontSize: '0.65rem' }} />}
                          </TableCell>
                          <TableCell>{p.numero}</TableCell>
                          <TableCell>{dayjs(p.vencimento).format('DD/MM/YYYY')}</TableCell>
                          <TableCell align="right">{Number(p.vparcela).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                  <TableFooter>
                    <TableRow>
                      <TableCell colSpan={3} sx={{ fontWeight: 700 }}>Total</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>
                        {parcelasParaApagar.reduce((acc, p) => acc + Number(p.vparcela), 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                      </TableCell>
                    </TableRow>
                  </TableFooter>
                </Table>
                <Typography variant="body2" sx={{ mt: 2 }}>
                  Tem certeza que deseja apagar as {parcelasParaApagar.length} parcelas?
                </Typography>
              </>
            ) : (
              <Typography variant="body2">Tem certeza que deseja excluir este registro?</Typography>
            )}
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button color="inherit" onClick={handleCancelDelete} disabled={apagando}>
              Cancelar
            </Button>
            <Button variant="contained" color="error" onClick={handleConfirmDelete} disabled={apagando}>
              {apagando ? 'Apagando...' : parcelasParaApagar.length > 1 ? `Apagar ${parcelasParaApagar.length} parcelas` : 'Apagar'}
            </Button>
          </DialogActions>
        </Dialog>
        <Box margin={1} display="flex" flexDirection="column" component={Paper} variant="outlined">
          <Grid container direction="column" padding={2} spacing={2}>
            {isLoading && (
              <Grid item>
                <LinearProgress variant='indeterminate' />
              </Grid>
            )}
            <Grid item>
              <Box display="flex" alignItems="center" gap={1}>
                <Typography variant='h6'>Dados</Typography>
                {filhos.length > 0 && <Chip label="Conta Consolidada" size="small" color="secondary" />}
              </Box>
            </Grid>
            <Grid container item direction="row" spacing={1}>
              <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                <Grid container item direction="row" spacing={1.5} >
                  <Grid item xs={12} sm={12} md={12} lg={6} xl={6} >
                    <AutoCompleteParceiro
                      isExternalLoading={bloqueioParcelamento ? true : isLoading}
                      nomeParceiro={(novoNome) => setNomeParceiro(novoNome || '')}
                    />
                  </Grid>
                  <Grid item xs={12} sm={12} md={9} lg={4} xl={4}>
                    <AutoCompletePlanoDeContas
                      isExternalLoading={bloqueioParcelamento ? true : isLoading}
                    />
                  </Grid>
                  <Grid item xs={6} sm={6} md={3} lg={2} xl={2}>
                    <VTextFieldData
                      fullWidth
                      size='small'
                      name='emissao'
                      label='Emissão'
                      disabled={bloqueioParcelamento ? true : isLoading}
                    />
                  </Grid>
                  <Grid item xs={6} sm={6} md={3} lg={2} xl={2}>
                    <VTextFieldData
                      fullWidth
                      size='small'
                      name='vencimento'
                      label='Vencimento'
                      // Com mais de 1 parcela, cada uma tem seu próprio vencimento
                      // editável na lista abaixo (ver listaParcelas).
                      disabled={modoMultiplasParcelas || (bloqueioParcelamento ? true : isLoading)}
                    />
                  </Grid>

                  <Grid item xs={6} sm={6} md={3} lg={2} xl={2}>
                    <VFieldMoney
                      fullWidth
                      size='small'
                      name='venda'
                      label='Valor Total'
                      calcular={handleValorTotal}
                      disabled={bloqueioParcelamento ? true : isLoading}
                    />
                  </Grid>
                  <Grid item xs={6} sm={6} md={3} lg={2} xl={2}>
                    {/* Campo único: escolhe 1x a 36x na lista ou digita a quantidade
                        (ou, numa parcela avulsa antiga, o rótulo livre como "1/3") —
                        ver handleParcelasTexto. */}
                    <Autocomplete
                      freeSolo
                      size='small'
                      options={OPCOES_QTD_PARCELAS}
                      value={parcelasTexto || null}
                      inputValue={parcelasTexto}
                      onChange={(_, valor) => handleParcelasTexto(valor ?? '')}
                      onInputChange={(_, valor, motivo) => {
                        if (motivo === 'input' || motivo === 'clear') handleParcelasTexto(valor);
                      }}
                      renderOption={(props, opcao) => <li {...props} key={opcao}>{opcao}x</li>}
                      disabled={bloqueioParcelamento ? true : isLoading}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          fullWidth
                          label='Parcelas'
                          placeholder='1'
                          inputProps={{ ...params.inputProps, inputMode: id !== 'novo' && !idlote ? 'text' : 'numeric' }}
                        />
                      )}
                    />
                    {/* Campo real ("parcela") sempre montado e escondido, pro unform não
                        perder o valor — quem o preenche é handleParcelasTexto/listaParcelas. */}
                    <Box sx={{ display: 'none' }}>
                      <VTextField name='parcela' />
                    </Box>
                  </Grid>
                  <Grid item xs={6} sm={6} md={3} lg={2} xl={2}>
                    <VFieldMoney
                      fullWidth
                      size='small'
                      name='vparcela'
                      label='Valor Parcela'
                      calcular={handlecalcularParcela}
                      // Com mais de 1 parcela, o valor de cada uma é definido na lista
                      // abaixo (ver listaParcelas) — esse campo vira só um resumo do
                      // 1º registro salvo, não faz sentido editar aqui.
                      disabled={modoMultiplasParcelas || (bloqueioParcelamento ? true : isLoading)}
                    />
                  </Grid>
                  <Grid item xs={6} sm={6} md={3} lg={2} xl={2}>
                    <VTextField
                      fullWidth
                      size='small'
                      name='numero'
                      label='Numero Nota'
                      disabled={bloqueioParcelamento ? true : isLoading}

                    />
                  </Grid>
                  <Grid item xs={6} sm={6} md={3} lg={2} xl={2}>
                    <VFieldMoney
                      fullWidth
                      size='small'
                      name='acrescimo'
                      label='Acréscimo / Juros'
                      calcular={handlecalcularAcescimo}
                      // Com mais de 1 parcela, esse total é dividido igualmente entre
                      // elas — ver listaParcelas e o useEffect que a gera. Uma vez que
                      // ESSA parcela já teve recebimento (total ou parcial), só o
                      // Desconto continua editável (ver bloqueioParcelamento/statusRecebidas).
                      disabled={(!statusVenda && statusRecebidas) ? true : isLoading}
                    />
                  </Grid>
                  <Grid item xs={6} sm={6} md={3} lg={2} xl={2}>
                    <VFieldMoney
                      fullWidth
                      size='small'
                      name='desconto'
                      label='Desconto'
                      calcular={handlecalcularDesconto}
                      disabled={isLoading}
                    />
                  </Grid>
                  <Grid item xs={6} sm={6} md={3} lg={2} xl={2}>
                    <VFieldMoney
                      fullWidth
                      size='small'
                      name='juros'
                      label='juros'
                      disabled={true}
                    />
                  </Grid>
                  <Grid item xs={6} sm={6} md={3} lg={2} xl={1}>
                    <VFieldMoney
                      fullWidth
                      size='small'
                      name='vrecebido'
                      label='Recebido'
                      disabled={true}
                    />
                  </Grid>
                  <Grid item xs={6} sm={6} md={3} lg={4} xl={1}>
                    <VFieldMoney
                      fullWidth
                      size='small'
                      name='vreceber'
                      label='Receber'
                      disabled={true}
                    />
                  </Grid>
                  <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                    <VTextField
                      fullWidth
                      name='historico'
                      multiline
                      rows={3}
                      disabled={(!statusVenda && statusRecebidas) ? true : isLoading}
                      label='Histórico'
                      placeholder='Ex.: Mensalidade do sistema referente a setembro/2026'
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Box>
      </VForm>
      {modoMultiplasParcelas && (
        <Box margin={1} display="flex" flexDirection="column" component={Paper} variant="outlined">
          <Grid container direction="column" padding={2} spacing={1.5}>
            <Grid item>
              <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                <Typography variant='h6'>Parcelas a gerar</Typography>
                <Chip label={`${listaParcelas.length} parcela(s)`} size="small" color="primary" variant="outlined" />
                <Chip
                  label={`Total das parcelas: ${formatarMoeda(somaListaParcelas)}`}
                  size="small"
                  variant="outlined"
                  color={Math.abs(diferencaListaParcelas) > 0.001 ? 'error' : 'success'}
                />
              </Box>
            </Grid>
            {id !== 'novo' && (
              <Grid item>
                <Alert severity="warning">
                  Ao salvar, essa parcela e todas as outras do mesmo lançamento serão apagadas e substituídas por essas {listaParcelas.length} parcelas novas.
                </Alert>
              </Grid>
            )}
            {(descontoTotal > 0 || acrescimoTotal > 0) && (
              <Grid item>
                <Typography variant='body2' color='text.secondary'>
                  Acréscimo ({formatarMoeda(acrescimoTotal)}) e desconto ({formatarMoeda(descontoTotal)}) do cabeçalho
                  divididos igualmente entre as {listaParcelas.length} parcelas — valor líquido esperado: {formatarMoeda(valorLiquidoEsperado)}.
                </Typography>
              </Grid>
            )}
            {Math.abs(diferencaListaParcelas) > 0.001 && (
              <Grid item>
                <Alert severity="warning">
                  A soma líquida das parcelas ({formatarMoeda(somaListaParcelas)}) não confere com o valor líquido esperado ({formatarMoeda(valorLiquidoEsperado)}).
                  Ajuste os valores abaixo antes de salvar.
                </Alert>
              </Grid>
            )}
            <Grid item>
              <List sx={{ p: 0 }}>
                {listaParcelas.map((item) => (
                  <ListItem
                    key={item.key}
                    sx={{
                      mb: 1,
                      p: 1.5,
                      borderRadius: 2,
                      border: '1px solid',
                      borderColor: 'divider',
                    }}
                  >
                    <Grid container spacing={1.5} alignItems="center">
                      <Grid item xs={4} sm={2} md={1.5}>
                        <TextField
                          size='small'
                          fullWidth
                          label='Parcela'
                          value={item.parcela}
                          disabled
                        />
                      </Grid>
                      <Grid item xs={8} sm={4} md={3}>
                        <TextField
                          size='small'
                          fullWidth
                          label='Nº Nota'
                          placeholder='Mesmo do lançamento'
                          value={item.numero}
                          onChange={(e) => handleParcelaGeradaNumero(item.key, e.target.value)}
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>
                      <Grid item xs={6} sm={3} md={3.5}>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                          <DatePicker
                            format='DD/MM/YYYY'
                            label='Vencimento'
                            value={dayjs(item.vencimento)}
                            onChange={(novoValor) => {
                              handleParcelaGeradaVencimento(item.key, dayjs(novoValor).format('YYYY-MM-DD'));
                            }}
                            slotProps={{ textField: { size: 'small', fullWidth: true } }}
                          />
                        </LocalizationProvider>
                      </Grid>
                      <Grid item xs={6} sm={3} md={4}>
                        <CampoValorParcela
                          valor={item.vparcela}
                          aoAlterar={(novoValor) => handleParcelaGeradaValor(item.key, String(novoValor))}
                        />
                      </Grid>
                      {(item.desconto > 0 || item.acrescimo > 0) && (
                        <Grid item xs={12}>
                          <Typography variant='caption' color='text.secondary'>
                            Desconto: {formatarMoeda(item.desconto)} · Acréscimo: {formatarMoeda(item.acrescimo)} · A receber: {formatarMoeda(arredondar2(item.vparcela - item.desconto + item.acrescimo))}
                          </Typography>
                        </Grid>
                      )}
                    </Grid>
                  </ListItem>
                ))}
              </List>
            </Grid>
          </Grid>
        </Box>
      )}
      {outrasParcelas.length > 0 && (
        <Box margin={1} display="flex" flexDirection="column" component={Paper} variant="outlined">
          <Grid container direction="column" padding={2} spacing={1.5}>
            <Grid item>
              <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                <Typography variant='h6'>Outras parcelas desse lançamento</Typography>
                <Chip label={`${outrasParcelas.length + 1} parcela(s) no total`} size="small" variant="outlined" />
              </Box>
            </Grid>
            {(statusRecebidas || outroTemPagamentoNoGrupo) && (
              <Grid item>
                <Alert severity="info">
                  {statusRecebidas
                    ? 'Essa parcela já teve recebimento — só é possível ajustar o Desconto.'
                    : 'Uma das outras parcelas desse lançamento já teve recebimento, por isso a quantidade de parcelas, vencimento, valor e parceiro ficaram travados aqui — só é possível ajustar Desconto, Acréscimo e Histórico.'}
                </Alert>
              </Grid>
            )}
            <Grid item>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Parcela</TableCell>
                      <TableCell align="center">Vencimento</TableCell>
                      <TableCell align="right">Valor Parcela</TableCell>
                      <TableCell align="right">Recebido</TableCell>
                      <TableCell align="right">A Receber</TableCell>
                      <TableCell align="center">Situação</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {outrasParcelas.map((parcela) => {
                      const paga = parcela.status;
                      const parcial = !paga && Number(parcela.vrecebido) > 0;
                      return (
                        <TableRow key={parcela.idrec}>
                          <TableCell sx={{ fontFamily: 'monospace' }}>{parcela.parcela || '—'}</TableCell>
                          <TableCell align="center" sx={{ fontFamily: 'monospace' }}>{dayjs(parcela.vencimento).format('DD/MM/YY')}</TableCell>
                          <TableCell align="right" sx={{ fontFamily: 'monospace' }}>{formatarMoeda(Number(parcela.vparcela))}</TableCell>
                          <TableCell align="right" sx={{ fontFamily: 'monospace' }}>{formatarMoeda(Number(parcela.vrecebido))}</TableCell>
                          <TableCell align="right" sx={{ fontFamily: 'monospace' }}>{formatarMoeda(Number(parcela.vreceber))}</TableCell>
                          <TableCell align="center">
                            <Chip
                              label={paga ? 'Pago' : parcial ? 'Parcial' : 'Em aberto'}
                              size="small"
                              color={paga ? 'success' : parcial ? 'warning' : 'default'}
                              variant={paga || parcial ? 'filled' : 'outlined'}
                            />
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
          </Grid>
        </Box>
      )}
      {filhos.length > 0 && (
        <Box margin={1} display="flex" flexDirection="column" component={Paper} variant="outlined">
          <Grid container direction="column" padding={2} spacing={1}>
            <Grid item>
              <Typography variant='h6'>Parcelas juntadas nessa conta</Typography>
            </Grid>
            <Grid item>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Número</TableCell>
                      <TableCell>Parcela</TableCell>
                      <TableCell align="center">Emissão</TableCell>
                      <TableCell align="center">Vencimento</TableCell>
                      <TableCell align="right">Valor Total</TableCell>
                      <TableCell align="right">Valor Parcela</TableCell>
                      <TableCell align="right">Juros/Acré.</TableCell>
                      <TableCell align="right">Recebido</TableCell>
                      <TableCell align="right">Desconto</TableCell>
                      <TableCell align="right">Valor Juntado</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filhos.map((filho, index) => (
                      <TableRow key={`${filho.numero}-${index}`}>
                        <TableCell sx={{ fontFamily: 'monospace' }}>{filho.numero}</TableCell>
                        <TableCell sx={{ fontFamily: 'monospace' }}>{filho.parcela || '—'}</TableCell>
                        <TableCell align="center" sx={{ fontFamily: 'monospace' }}>{dayjs(filho.emissao).format('DD/MM/YY')}</TableCell>
                        <TableCell align="center" sx={{ fontFamily: 'monospace' }}>{dayjs(filho.vencimento).format('DD/MM/YY')}</TableCell>
                        <TableCell align="right" sx={{ fontFamily: 'monospace' }}>{formatarMoeda(Number(filho.venda))}</TableCell>
                        <TableCell align="right" sx={{ fontFamily: 'monospace' }}>{formatarMoeda(Number(filho.vparcela))}</TableCell>
                        <TableCell align="right" sx={{ fontFamily: 'monospace' }}>{formatarMoeda(Number(filho.acrescimo))}</TableCell>
                        <TableCell align="right" sx={{ fontFamily: 'monospace' }}>{formatarMoeda(Number(filho.vrecebido))}</TableCell>
                        <TableCell align="right" sx={{ fontFamily: 'monospace' }}>{formatarMoeda(Number(filho.desconto))}</TableCell>
                        <TableCell align="right" sx={{ fontFamily: 'monospace', fontWeight: 700 }}>{formatarMoeda(Number(filho.valor))}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
          </Grid>
        </Box>
      )}
      {recebidosHistorico.length > 0 && (
        <Box margin={1} display="flex" flexDirection="column" component={Paper} variant="outlined">
          <Grid container direction="column" padding={2} spacing={1}>
            <Grid item>
              <Typography variant='h6'>Comprovantes de pagamento</Typography>
            </Grid>
            <Grid item>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Data</TableCell>
                      <TableCell>Conta</TableCell>
                      <TableCell align="right">Valor</TableCell>
                      <TableCell align="center">Comprovante</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {recebidosHistorico.map((item) => (
                      <TableRow key={item.idrecebida}>
                        <TableCell sx={{ fontFamily: 'monospace' }}>{dayjs(item.dataoperacao).format('DD/MM/YY HH:mm')}</TableCell>
                        <TableCell sx={{ fontFamily: 'monospace' }}>{item.nomeConta || '—'}</TableCell>
                        <TableCell align="right" sx={{ fontFamily: 'monospace', fontWeight: 700 }}>{formatarMoeda(Number(item.valor))}</TableCell>
                        <TableCell align="center">
                          <Tooltip title="Reimprimir comprovante">
                            <IconButton size="small" onClick={() => handleReimprimir(item)}>
                              <PrintIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
          </Grid>
        </Box>
      )}
      </LayoutBaseDePagina>
      <DialogVisualizarComprovante
        open={openComprovante}
        dados={comprovanteDados}
        onClose={() => setOpenComprovante(false)}
      />
    </>
  );
};